import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    IconButton,
    Collapse,
    Grid,
    Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InventoryIcon from '@mui/icons-material/Inventory';

const OrderCard = ({ order }) => {
    const { t } = useTranslation("global");
    const [expanded, setExpanded] = useState(false);

    const getStatusColor = (status) => {
        const statusColors = {
            'A TIEMPO': 'success',
            'ATRASADO': 'error',
            'ANTICIPADO': 'info',
            'SIN ESTADO': 'default'
        };
        return statusColors[status] || 'default';
    };

    const formatCurrency = (value) => {
        return Math.round(value).toLocaleString('es-CO', {
            style: 'currency',
            currency: 'COP'
        });
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <Card
            elevation={2}
            sx={{
                mb: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)'
                },
                borderLeft: `4px solid ${getStatusColor(order.estado) === 'success' ? '#22C55E' :
                    getStatusColor(order.estado) === 'error' ? '#EF4444' :
                        getStatusColor(order.estado) === 'info' ? '#3B82F6' : '#9CA3AF'
                    }`
            }}
        >
            <CardContent>
                {/* Header compacto */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1F2937' }}>
                                OC #{order.IdPedido}
                            </Typography>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <CalendarTodayIcon sx={{ fontSize: 16 }} />
                            {t("ordenes.card.entrega")}: {formatDate(order.fechaentrega)}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOnIcon sx={{ fontSize: 16 }} />
                            {order.Sede}
                        </Typography>
                    </Box>

                    <IconButton
                        onClick={() => setExpanded(!expanded)}
                        sx={{
                            backgroundColor: '#F3F4F6',
                            '&:hover': { backgroundColor: '#E5E7EB' }
                        }}
                    >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>

                {/* Info del producto - siempre visible */}
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <InventoryIcon sx={{ fontSize: 18, color: '#6B7280' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                            {order.ProductoId}
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
                        {order.DescProducto}
                    </Typography>
                </Box>

                {/* Valores principales */}
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">{t("ordenes.card.cantidad")}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {parseFloat(order.CantidadPedida).toFixed(2)} {order.Unidad}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">{t("ordenes.card.valor_total")}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#BD1F34' }}>
                            {formatCurrency(order.ValorNeto)}
                        </Typography>
                    </Grid>
                </Grid>

                {/* Detalles expandibles */}
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">{t("ordenes.card.fecha_generacion")}</Typography>
                            <Typography variant="body2">{formatDate(order.FechaCreacion)}</Typography>
                        </Grid>


                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">{t("ordenes.card.estado_articulo")}</Typography>
                            <Typography variant="body2">{order.EstadoArticulo}</Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">{t("ordenes.card.estado_pedido")}</Typography>
                            <Typography variant="body2">{order.EstadoPedido}</Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">{t("ordenes.card.part_number")}</Typography>
                            <Typography variant="body2">{order.NumeroPiezaProveedor}</Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">{t("ordenes.card.posicion")}</Typography>
                            <Typography variant="body2">{order.PosicionPedido}</Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">{t("ordenes.card.cantidad_entregada")}</Typography>
                            <Typography variant="body2">{parseFloat(order.CantidadEntrega).toFixed(2)}</Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">{t("ordenes.card.valor_unitario")}</Typography>
                            <Typography variant="body2">{formatCurrency(order.PrecioNeto)}</Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Cantidad Restante</Typography>
                            <Typography variant="body2">{parseFloat((order.CantidadPedida || 0) - (order.CantidadEntrega || 0)).toFixed(2)}</Typography>
                        </Grid>


                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">{t("ordenes.card.categoria")}</Typography>
                            <Typography variant="body2">{order.Categoria}</Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">{t("ordenes.card.proveedor")}</Typography>
                            <Typography variant="body2">{order.Proveedor}</Typography>
                        </Grid>
                    </Grid>
                </Collapse>
            </CardContent>
        </Card>
    );
};

export default OrderCard;
